import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, FormControlLabel, Switch, TextField } from '@mui/material';
import { PageCard } from '../CardWithTitle';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { Account, CustomFieldDefinition } from '../../../types';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import {
  createCustomFieldDefinitionApi,
  fetchCustomFieldDefinitionsApi,
  updateCustomFieldDefinitionApi
} from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import {
  fetchCustomFieldSettingApi,
  updateCustomFieldSettingApi
} from '../../../Api/Admin/CustomFields/customFieldSettingsApi';
import { extractApiErrorMessage } from '../McpAccess/extractApiErrorMessage';
import { DataTypeEditor } from '../CustomFieldsForm/DataTypeEditor';
import { DATA_TYPE_LIST, DATA_TYPE_TEXT, DataTypeValue, normaliseFieldType } from '../../../utils/customFieldTypes';
import { useChangeFieldTypeWithConfirm } from '../CustomFieldsForm/useChangeFieldTypeWithConfirm';
import { saveFieldTypeOrChoices } from '../CustomFieldsForm/saveFieldTypeOrChoices';
import { getUser, isCapabilityDenied } from '../../../store/User';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';
import { TextSpan } from '../../Atoms/Text';

const DESCRIPTION =
  'Add custom fields to the Projects tables. Each field has a label and a data type: Text for any wording, ' +
  'Date for a single calendar day, Yes/No for one of those two answers, or List for one choice from a set ' +
  'you supply. Fields are scoped independently to Projects and the Related Inventory table.';

const DISPLAY_ON_INVENTORY_INHERITED_NOTE =
  "Inherited from this account's parent in the portal's Sub Accounts list. Changing it here gives this account its own setting.";

const ENTITY_TYPE_PROJECT = 'Project';
const ENTITY_TYPE_PROJECT_DETAIL = 'ProjectDetail';

type ProjectEntityType = typeof ENTITY_TYPE_PROJECT | typeof ENTITY_TYPE_PROJECT_DETAIL;

const ENTITY_TYPES: ProjectEntityType[] = [ENTITY_TYPE_PROJECT, ENTITY_TYPE_PROJECT_DETAIL];

const TAB_LABELS: Record<ProjectEntityType, string> = {
  Project: 'Projects',
  ProjectDetail: 'Projects - Related Inventory'
};

const TABS: { key: ProjectEntityType; label: string }[] = ENTITY_TYPES.map((key) => ({
  key,
  label: TAB_LABELS[key]
}));

interface FieldRow {
  key: string;
  id: string | null;
  name: string;
  fieldType: DataTypeValue;
  choices: string[];
  isEditable: boolean;
  isNew: boolean;
  removed: boolean;
  originalName: string;
  originalFieldType: DataTypeValue;
  originalChoicesKey: string;
  originalIsEditable: boolean;
}

const choicesKey = (choices: string[]) => JSON.stringify(choices);

const toRow = (def: CustomFieldDefinition): FieldRow => {
  const fieldType = normaliseFieldType(def.field_type);
  const choices = def.options?.choices ?? [];

  return {
    key: def.id,
    id: def.id,
    name: def.name,
    fieldType,
    choices,
    isEditable: def.is_editable,
    isNew: false,
    removed: false,
    originalName: def.name,
    originalFieldType: fieldType,
    originalChoicesKey: choicesKey(choices),
    originalIsEditable: def.is_editable
  };
};

const rowValid = (row: FieldRow) =>
  row.name.trim() !== '' && (row.fieldType !== DATA_TYPE_LIST || row.choices.length > 0);

const hasDuplicateNames = (rows: FieldRow[]) => {
  const names = rows.filter((row) => !row.removed).map((row) => row.name.trim().toLowerCase());
  return new Set(names).size !== names.length;
};

// Labels shared across both scopes render twice (once per section) on a 1:1 project detail page,
// so we surface a non-blocking heads-up. Returns the original-cased Project labels that collide.
const crossScopeDuplicateLabels = (rowsByType: Record<ProjectEntityType, FieldRow[]>): string[] => {
  const activeLabels = (rows: FieldRow[]) =>
    rows.filter((row) => !row.removed && row.name.trim() !== '').map((row) => row.name.trim());
  const detailLabelsLower = new Set(activeLabels(rowsByType.ProjectDetail).map((name) => name.toLowerCase()));
  const seen = new Set<string>();
  return activeLabels(rowsByType.Project).filter((label) => {
    const lower = label.toLowerCase();
    if (!detailLabelsLower.has(lower) || seen.has(lower)) return false;
    seen.add(lower);
    return true;
  });
};

const TAB_BASE_STYLE = 'rounded-lg text-sm font-normal py-2 px-4';
const ACTIVE_TAB_STYLE = `${TAB_BASE_STYLE} bg-main-100 text-white`;
const INACTIVE_TAB_STYLE = `${TAB_BASE_STYLE} border border-gray-300 text-gray-700 hover:bg-gray-50`;

export interface CustomFieldsConfigurationFormProps {
  account: Account;
  onClose: () => void;
}

export const CustomFieldsConfigurationForm = ({ account, onClose }: CustomFieldsConfigurationFormProps) => {
  const { setSnack } = useSnackbar();
  const [rowsByType, setRowsByType] = useState<Record<ProjectEntityType, FieldRow[]>>({
    Project: [],
    ProjectDetail: []
  });
  const [activeTab, setActiveTab] = useState<ProjectEntityType>(ENTITY_TYPE_PROJECT);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [displayOnInventory, setDisplayOnInventory] = useState(false);
  const [savedDisplayOnInventory, setSavedDisplayOnInventory] = useState(false);
  const [settingUnavailable, setSettingUnavailable] = useState(false);
  const [settingInherited, setSettingInherited] = useState(false);
  const newRowCounter = useRef(0);
  const { change: changeFieldTypeWithConfirm, dialog: incompatibleValuesDialog } = useChangeFieldTypeWithConfirm();
  const createDenied = isCapabilityDenied(CustomFieldCapability.DEFINITIONS_CREATE, getUser());
  const updateDenied = isCapabilityDenied(CustomFieldCapability.DEFINITIONS_UPDATE, getUser());
  const changeTypeDenied = isCapabilityDenied(CustomFieldCapability.DEFINITIONS_CHANGE_FIELD_TYPE, getUser());

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const [projectRes, detailRes, settingRes] = await Promise.all([
      fetchCustomFieldDefinitionsApi(account.id, undefined, ENTITY_TYPE_PROJECT),
      fetchCustomFieldDefinitionsApi(account.id, undefined, ENTITY_TYPE_PROJECT_DETAIL),
      fetchCustomFieldSettingApi(account.id, ENTITY_TYPE_PROJECT_DETAIL)
    ]);
    const error = projectRes.error || detailRes.error;
    if (error) {
      setLoadError(extractApiErrorMessage(error, 'Could not load custom fields'));
    } else {
      setRowsByType({
        Project: (projectRes.data ?? []).map(toRow),
        ProjectDetail: (detailRes.data ?? []).map(toRow)
      });
      // One checkbox is no reason to lock the fields; it stays off and unchangeable until a reload reads it.
      setSettingUnavailable(!!settingRes.error);
      const saved = settingRes.data?.display_on_inventory ?? false;
      setDisplayOnInventory(saved);
      setSavedDisplayOnInventory(saved);
      setSettingInherited(settingRes.data?.inherited ?? false);
    }
    setLoading(false);
  }, [account.id]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updateRow = (entityType: ProjectEntityType, key: string, patch: Partial<FieldRow>) => {
    setRowsByType((current) => ({
      ...current,
      [entityType]: current[entityType].map((row) => (row.key === key ? { ...row, ...patch } : row))
    }));
  };

  const addRow = (entityType: ProjectEntityType) => {
    newRowCounter.current += 1;
    const newRow: FieldRow = {
      key: `new-${newRowCounter.current}`,
      id: null,
      name: '',
      fieldType: DATA_TYPE_TEXT,
      choices: [],
      // Project scopes start locked, mirroring the api's per-entity-type default.
      isEditable: false,
      isNew: true,
      removed: false,
      originalName: '',
      originalFieldType: DATA_TYPE_TEXT,
      originalChoicesKey: choicesKey([]),
      originalIsEditable: false
    };
    setRowsByType((current) => ({ ...current, [entityType]: [...current[entityType], newRow] }));
  };

  const removeRow = (entityType: ProjectEntityType, key: string) => {
    setRowsByType((current) => ({
      ...current,
      [entityType]: current[entityType].flatMap((row) => {
        if (row.key !== key) return [row];
        if (row.isNew) return [];
        return [{ ...row, removed: true }];
      })
    }));
  };

  const duplicatesExist = hasDuplicateNames(rowsByType.Project) || hasDuplicateNames(rowsByType.ProjectDetail);
  const crossScopeDuplicates = crossScopeDuplicateLabels(rowsByType);
  const allActiveRows = [...rowsByType.Project, ...rowsByType.ProjectDetail].filter((row) => !row.removed);
  const canSave = !loading && !saving && !duplicatesExist && allActiveRows.every(rowValid);

  const dropRow = (entityType: ProjectEntityType, key: string) => {
    setRowsByType((current) => ({
      ...current,
      [entityType]: current[entityType].filter((row) => row.key !== key)
    }));
  };

  type SaveRowResult = { error: Parameters<typeof extractApiErrorMessage>[0]; cancelled?: boolean };

  // Rows re-baseline after each successful call: a retried create duplicates, a retried type change 422s.
  const saveRow = async (entityType: ProjectEntityType, row: FieldRow): Promise<SaveRowResult> => {
    if (row.isNew) {
      if (row.removed) return { error: null };
      const options = row.fieldType === DATA_TYPE_LIST ? { choices: row.choices } : undefined;
      const { data, error } = await createCustomFieldDefinitionApi(account.id, {
        name: row.name.trim(),
        entity_type: entityType,
        field_type: row.fieldType,
        is_editable: row.isEditable,
        options
      });
      if (error) return { error };
      updateRow(entityType, row.key, {
        id: data?.id ?? null,
        isNew: !data?.id,
        name: row.name.trim(),
        originalName: row.name.trim(),
        originalFieldType: row.fieldType,
        originalChoicesKey: choicesKey(row.choices),
        originalIsEditable: row.isEditable
      });
      return { error: null };
    }

    if (!row.id) return { error: null };
    if (row.removed) {
      const { error } = await updateCustomFieldDefinitionApi(row.id, { is_archived: true });
      if (error) return { error };
      dropRow(entityType, row.key);
      return { error: null };
    }

    // The dedicated endpoint 409s on incompatible stored values so the hook can confirm clearing them.
    const typeOrChoices = await saveFieldTypeOrChoices(
      row.id,
      row,
      choicesKey(row.choices) !== row.originalChoicesKey,
      changeFieldTypeWithConfirm
    );
    if (typeOrChoices) {
      if (typeOrChoices.error || typeOrChoices.cancelled) {
        return { error: typeOrChoices.error, cancelled: typeOrChoices.cancelled };
      }
      updateRow(entityType, row.key, {
        originalFieldType: row.fieldType,
        originalChoicesKey: choicesKey(row.choices)
      });
    }

    if (row.name.trim() !== row.originalName) {
      const { error } = await updateCustomFieldDefinitionApi(row.id, { name: row.name.trim() });
      if (error) return { error };
      updateRow(entityType, row.key, { originalName: row.name.trim() });
    }

    if (row.isEditable !== row.originalIsEditable) {
      const { error } = await updateCustomFieldDefinitionApi(row.id, { is_editable: row.isEditable });
      if (error) return { error };
      updateRow(entityType, row.key, { originalIsEditable: row.isEditable });
    }

    return { error: null };
  };

  const rowDirty = (row: FieldRow) =>
    (row.isNew && !row.removed) ||
    (!row.isNew &&
      !!row.id &&
      (row.removed ||
        row.name.trim() !== row.originalName ||
        row.fieldType !== row.originalFieldType ||
        row.isEditable !== row.originalIsEditable ||
        (row.fieldType === DATA_TYPE_LIST && choicesKey(row.choices) !== row.originalChoicesKey)));

  const failSave = (error: SaveRowResult['error'], fallback = 'Could not save custom fields') => {
    setSnack({
      message: extractApiErrorMessage(error, fallback),
      type: 'error',
      open: true
    });
    setSaving(false);
  };

  const handleSave = async () => {
    setSaving(true);

    const dirtyRows = ENTITY_TYPES.flatMap((entityType) =>
      rowsByType[entityType].filter(rowDirty).map((row) => ({ entityType, row }))
    );

    const settingDirty = displayOnInventory !== savedDisplayOnInventory;

    if (dirtyRows.length === 0 && !settingDirty) {
      setSnack({ message: `Custom fields saved for ${account.name}`, type: 'success', open: true });
      setSaving(false);
      return;
    }

    // Ahead of the fields: a declined type-change dialog ends the save there, and must not take this change with it.
    if (settingDirty) {
      const { error } = await updateCustomFieldSettingApi(account.id, ENTITY_TYPE_PROJECT_DETAIL, displayOnInventory);
      if (error) {
        failSave(error, 'Could not save the Inventory page setting');
        return;
      }
      setSavedDisplayOnInventory(displayOnInventory);
      setSettingInherited(false);
    }

    // Sequential so a type-change confirm dialog resolves before the next call runs.
    for (const { entityType, row } of dirtyRows) {
      const { error, cancelled } = await saveRow(entityType, row);
      if (cancelled) {
        // Declined dialog: stop quietly; re-baselined rows let a later Save resume here.
        setSaving(false);
        return;
      }
      if (error) {
        failSave(error);
        return;
      }
    }

    setSnack({ message: `Custom fields saved for ${account.name}`, type: 'success', open: true });
    setSaving(false);
    await loadConfig();
  };

  const displayRows = rowsByType[activeTab].filter((row) => !row.removed);

  return (
    <PageCard
      title="Custom Fields"
      dataCy="custom-fields-config-card"
      enableCollapse={false}>
      {loading && <ComponentSpinner sx={{ marginTop: '2rem', marginBottom: '2rem' }} />}
      {!loading && loadError && (
        <Alert
          severity="error"
          data-testid="custom-fields-load-error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={loadConfig}
              data-testid="custom-fields-load-retry">
              Retry
            </Button>
          }>
          {loadError}
        </Alert>
      )}
      {!loading && !loadError && (
        <>
          <p className="text-sm text-gray-500 mb-4">{DESCRIPTION}</p>
          <div className="flex items-center gap-2 mb-6">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={activeTab === tab.key ? ACTIVE_TAB_STYLE : INACTIVE_TAB_STYLE}
                data-cy={`custom-fields-tab-${tab.key}`}>
                {tab.label}
              </button>
            ))}
            {activeTab === ENTITY_TYPE_PROJECT_DETAIL && (
              <FormControlLabel
                className="ml-4"
                disabled={settingUnavailable || updateDenied}
                control={
                  <Checkbox
                    size="small"
                    checked={displayOnInventory}
                    onChange={(e) => setDisplayOnInventory(e.target.checked)}
                    data-cy="custom-fields-display-on-inventory"
                  />
                }
                label={
                  <TextSpan
                    size="sm"
                    color="grey8">
                    Display Custom Fields on the Inventory Page as well
                  </TextSpan>
                }
              />
            )}
            {activeTab === ENTITY_TYPE_PROJECT_DETAIL && settingUnavailable && (
              <TextSpan
                size="sm"
                color="red"
                dataCy="custom-fields-display-on-inventory-error">
                Could not load this setting
              </TextSpan>
            )}
          </div>
          {activeTab === ENTITY_TYPE_PROJECT_DETAIL &&
            settingInherited &&
            displayOnInventory === savedDisplayOnInventory && (
              <p
                className="text-sm text-gray-500 -mt-4 mb-6"
                data-testid="custom-fields-display-on-inventory-inherited-note">
                {DISPLAY_ON_INVENTORY_INHERITED_NOTE}
              </p>
            )}
          <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-6 pb-2">
            <TextSpan
              size="sm"
              weight="medium"
              color="grey8">
              Field Label
            </TextSpan>
            <TextSpan
              size="sm"
              weight="medium"
              color="grey8">
              Data Type
            </TextSpan>
            <div className="w-20 text-center">
              <TextSpan
                size="sm"
                weight="medium"
                color="grey8">
                Editable
              </TextSpan>
            </div>
            <span className="w-16" />
          </div>
          {displayRows.map((row, index) => (
            <div
              key={row.key}
              className="grid grid-cols-[1fr_1fr_auto_auto] gap-6 items-start py-3 border-t border-gray-200"
              data-cy="custom-field-row">
              <TextField
                id={`custom-field-label-${activeTab}-${index}`}
                size="small"
                value={row.name}
                disabled={!row.isNew && updateDenied}
                onChange={(e) => updateRow(activeTab, row.key, { name: e.target.value })}
                inputProps={{ 'aria-label': `Field Label ${index + 1}` }}
                sx={{ width: '100%' }}
              />
              <DataTypeEditor
                fieldType={row.fieldType}
                choices={row.choices}
                onFieldTypeChange={(fieldType) => updateRow(activeTab, row.key, { fieldType })}
                onChoicesChange={(choices) => updateRow(activeTab, row.key, { choices })}
                readOnly={!row.isNew && changeTypeDenied}
                choicesReadOnly={!row.isNew && updateDenied && row.fieldType === row.originalFieldType}
                ariaLabel={`Data Type ${index + 1}`}
              />
              <div className="flex h-10 w-20 items-center justify-center">
                <Switch
                  size="small"
                  checked={row.isEditable}
                  disabled={!row.isNew && updateDenied}
                  onChange={() => updateRow(activeTab, row.key, { isEditable: !row.isEditable })}
                  sx={{
                    '.Mui-checked': { color: '#3B6FE8 !important' },
                    '.MuiSwitch-track': { backgroundColor: '#878EBE !important' }
                  }}
                  inputProps={{ 'aria-label': `Editable ${index + 1}` }}
                />
              </div>
              {(row.isNew || !updateDenied) && (
                <button
                  onClick={() => removeRow(activeTab, row.key)}
                  className="text-sm text-red-600 hover:text-red-700 w-16 text-left mt-2"
                  data-cy="custom-field-remove-btn">
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() => addRow(activeTab)}
            disabled={createDenied}
            className="mt-4 text-sm text-main-100 font-medium bg-secondary-100 hover:bg-secondary-200 rounded-lg py-2 px-4 disabled:opacity-50"
            data-cy="custom-field-add-btn">
            + Add Custom Field
          </button>
          {duplicatesExist && (
            <p
              className="text-sm text-red-600 mt-4"
              data-cy="custom-fields-duplicate-warning">
              Field labels must be unique within each table.
            </p>
          )}
          {crossScopeDuplicates.length > 0 && (
            <Alert
              severity="warning"
              className="mt-4"
              data-cy="custom-fields-cross-scope-warning"
              data-testid="custom-fields-cross-scope-warning">
              {`These labels are used on both Projects and Related Inventory: ${crossScopeDuplicates.join(', ')}. `}
              They will appear as separate fields on a project&apos;s detail page.
            </Alert>
          )}
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-6 pr-8 mt-6">
            <button
              onClick={onClose}
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              data-cy="custom-fields-cancel-btn">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              data-cy="custom-fields-save-btn">
              Save
            </button>
          </div>
          {incompatibleValuesDialog}
        </>
      )}
    </PageCard>
  );
};
